import { useState, useRef, useEffect } from "react";

interface DropDownProps {
  id: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  label: string;
  options: { value: string; label: string }[];
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  allowNew?: boolean;
}

export function DropDown({
  id,
  name,
  value,
  onChange,
  label,
  options,
  error,
  disabled,
  placeholder,
  allowNew = false,
}: DropDownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    if (allowNew) {
      // Filter options that match the input
      const filtered = options.filter((option) =>
        option.label.toLowerCase().includes(newValue.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  };

  const handleSelect = (option: { value: string; label: string }) => {
    setInputValue(option.label);
    onChange({
      target: {
        name,
        value: option.value,
      },
    } as React.ChangeEvent<HTMLSelectElement>);
    setIsOpen(false);
  };

  const handleCreateNew = () => {
    setInputValue(inputValue);
    onChange({
      target: {
        name,
        value: inputValue,
      },
    } as React.ChangeEvent<HTMLSelectElement>);
    setIsOpen(false);
  };

  const handleInputBlur = () => {
    // If allowNew is true and the input value doesn't match any option,
    // create a new option
    if (
      allowNew &&
      inputValue &&
      !options.some((opt) => opt.value === inputValue)
    ) {
      handleCreateNew();
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label
        htmlFor={id}
        className="block text-xs font-medium text-gray-500 mb-1"
      >
        {label}
      </label>
      <div className="relative">
        <input
          type="text"
          id={id}
          name={name}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onFocus={() => setIsOpen(true)}
          disabled={disabled}
          placeholder={placeholder}
          className={`block w-full rounded-lg border ${
            error ? "border-red-300" : "border-gray-300"
          } px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500`}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg
            className="h-5 w-5 text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      </div>
      {isOpen && !disabled && (
        <div className="absolute z-10 mt-1 w-full rounded-md bg-white shadow-lg">
          <ul className="max-h-60 overflow-auto rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {filteredOptions.map((option) => (
              <li
                key={option.value}
                className="relative cursor-pointer select-none py-2 pl-3 pr-9 hover:bg-blue-50"
                onClick={() => handleSelect(option)}
              >
                <span className="block truncate">{option.label}</span>
              </li>
            ))}
            {allowNew &&
              inputValue &&
              !options.some((opt) => opt.value === inputValue) && (
                <li
                  className="relative cursor-pointer select-none py-2 pl-3 pr-9 hover:bg-blue-50 text-blue-600"
                  onClick={handleCreateNew}
                >
                  <span className="block truncate">Create "{inputValue}"</span>
                </li>
              )}
          </ul>
        </div>
      )}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}
